'use client';

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'sub_admin' | 'sales_team' | 'pending';
  is_active: boolean;
  avatar_url?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: any | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
  refreshProfile: () => Promise<void>;
  isAdmin: () => boolean;
  isSubAdmin: () => boolean;
  isSalesTeam: () => boolean;
  isAdminOrSubAdmin: () => boolean;
  hasRole: (role: string) => boolean;
  canAccessCMS: () => boolean;
  canManageUsers: () => boolean;
  canAssignLeads: () => boolean;
  canViewAllLeads: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  
  // Prevent duplicate operations
  const fetchingProfile = useRef(false);
  const initializingAuth = useRef(false);
  const mounted = useRef(true);

  // Retry limits
  const MAX_AUTH_RETRIES = 5;
  const AUTH_RETRY_COOLDOWN = 5000; // 5 seconds
  const authRetryCount = useRef(0);
  const lastAuthAttempt = useRef<number | null>(null);

  useEffect(() => {
    mounted.current = true;
    
    // Prevent multiple simultaneous initializations
    if (initializingAuth.current) {
      return;
    }
    
    initializingAuth.current = true;
    
    const initializeAuth = async () => {
      try {
        // Check retry limits
        const now = Date.now();
        if (authRetryCount.current >= MAX_AUTH_RETRIES) {
          const timeSinceLastAttempt = now - lastAuthAttempt.current;
          if (timeSinceLastAttempt < AUTH_RETRY_COOLDOWN) {
            console.warn('[AuthContext] Max retries reached, waiting for cooldown');
            setSession(null);
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          } else {
            // Reset retry count after cooldown
            authRetryCount.current = 0;
          }
        }
        
        lastAuthAttempt.current = now;
        authRetryCount.current++;
        
        const client = createClient();
        
        // Get initial session with error handling
        try {
          const { data: { session }, error } = await client.auth.getSession();
          
          if (!mounted.current) return;
          
          // Handle errors gracefully
          if (error) {
            console.warn('[AuthContext] Session error:', error.message);
            
            // Check if it's a token error
            if (error.message.includes('refresh') || error.message.includes('token')) {
              console.warn('[AuthContext] Token error detected, clearing session');
              // Don't retry on token errors
              authRetryCount.current = MAX_AUTH_RETRIES;
            }
            
            setSession(null);
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            return;
          }
          
          // Success - reset retry count
          if (session) {
            authRetryCount.current = 0;
          }
          
          setSession(session);
          setUser(session?.user ?? null);
          
          // Fetch user profile if session exists
          if (session?.user && mounted.current) {
            await fetchUserProfile(client, session.user.id);
          } else {
            setLoading(false);
          }
        } catch (error: any) {
          if (!mounted.current) return;
          
          console.warn('[AuthContext] Session initialization error:', error.message);
          
          // Check if it's a critical error
          if (error.message.includes('refresh') || error.message.includes('token') || error.message.includes('abort')) {
            authRetryCount.current = MAX_AUTH_RETRIES; // Stop retrying
          }
          
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }

        // Listen for auth changes
        const { data: { subscription } } = client.auth.onAuthStateChange(
          async (event, session) => {
            if (!mounted.current) return;
            
            console.log('[AuthContext] Auth state changed:', event);
            
            // Reset retry count on successful auth events
            if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
              if (session) {
                authRetryCount.current = 0;
              }
            }
            
            // Stop retrying on sign out or token errors
            if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' && !session) {
              authRetryCount.current = MAX_AUTH_RETRIES;
            }
            
            setSession(session);
            setUser(session?.user ?? null);
            
            // Fetch user profile on auth change
            if (session?.user && mounted.current) {
              await fetchUserProfile(client, session.user.id);
            } else {
              setUserProfile(null);
              setLoading(false);
            }
          }
        );

        // Set up real-time subscription for user profile changes
        let profileSubscription: any = null;
        
        try {
          const { data: { session } } = await client.auth.getSession();
          if (session?.user && mounted.current) {
            profileSubscription = client
              .channel('user_profile_changes')
              .on(
                'postgres_changes',
                {
                  event: 'UPDATE',
                  schema: 'public',
                  table: 'user_profiles',
                  filter: `id=eq.${session.user.id}`
                },
                (payload) => {
                  if (mounted.current && payload.new) {
                    console.log('[AuthContext] Profile updated:', payload.new);
                    setUserProfile(payload.new as UserProfile);
                  }
                }
              )
              .subscribe();
          }
        } catch (error) {
          console.warn('[AuthContext] Profile subscription error:', error);
        }

        return () => {
          mounted.current = false;
          subscription.unsubscribe();
          if (profileSubscription) {
            profileSubscription.unsubscribe();
          }
        };
      } catch (error) {
        console.error('[AuthContext] Initialization error:', error);
        if (mounted.current) {
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      } finally {
        initializingAuth.current = false;
      }
    };
    
    const cleanup = initializeAuth();
    
    return () => {
      mounted.current = false;
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => {
          if (cleanupFn && typeof cleanupFn === 'function') {
            cleanupFn();
          }
        });
      }
    };
  }, []);

  // Fetch user profile from database with duplicate prevention
  const fetchUserProfile = async (client: any, userId: string) => {
    // Prevent duplicate fetches
    if (fetchingProfile.current) {
      return;
    }

    fetchingProfile.current = true;

    try {
      const { data, error } = await client
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (!mounted.current) return;
      
      if (error) {
        if (error.code !== 'PGRST116') {
          console.error('[AuthContext] Profile fetch error:', error);
        }
        setUserProfile(null);
      } else if (!data) {
        console.warn('[AuthContext] No profile found for user:', userId);
        setUserProfile(null);
      } else {
        setUserProfile(data);
      }
    } catch (error: any) {
      if (!mounted.current) return;
      console.error('[AuthContext] Profile fetch exception:', error);
      setUserProfile(null);
    } finally {
      setLoading(false);
      fetchingProfile.current = false;
    }
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const supabase = createClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || '',
          role: metadata?.role || 'pending',
          is_active: metadata?.isActive || false
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      // The onAuthStateChange listener will handle profile fetching
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Sign Out
  const signOut = async () => {
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUserProfile(null);
    } catch (error) {
      throw error;
    }
  };

  // Get Current User
  const getCurrentUser = async () => {
    const supabase = createClient();
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Refresh user profile
  const refreshProfile = async () => {
    if (!user) return;
    const supabase = createClient();
    await fetchUserProfile(supabase, user.id);
  };

  // Role checking methods
  const isAdmin = () => {
    if (!user || !userProfile) return false;
    return userProfile?.role === 'admin';
  };

  const isSubAdmin = () => {
    if (!user || !userProfile) return false;
    return userProfile?.role === 'sub_admin';
  };

  const isSalesTeam = () => {
    if (!user || !userProfile) return false;
    return userProfile?.role === 'sales_team';
  };

  const isAdminOrSubAdmin = () => {
    if (!user || !userProfile) return false;
    return userProfile?.role === 'admin' || userProfile?.role === 'sub_admin';
  };

  const hasRole = (role: string) => {
    if (!user || !userProfile) return false;
    return userProfile?.role === role;
  };

  // Check if user can access CMS
  const canAccessCMS = () => {
    if (!userProfile) return false;
    return userProfile.role === 'admin' || userProfile.role === 'sub_admin';
  };

  // Check if user can manage users
  const canManageUsers = () => {
    if (!userProfile) return false;
    return userProfile.role === 'admin';
  };

  // Check if user can assign leads
  const canAssignLeads = () => {
    if (!userProfile) return false;
    return userProfile.role === 'admin' || userProfile.role === 'sub_admin';
  };

  // Check if user can view all leads
  const canViewAllLeads = () => {
    if (!userProfile) return false;
    return userProfile.role === 'admin' || userProfile.role === 'sub_admin';
  };

  const value = {
    user,
    session,
    loading,
    userProfile,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshProfile,
    isAdmin,
    isSubAdmin,
    isSalesTeam,
    isAdminOrSubAdmin,
    hasRole,
    canAccessCMS,
    canManageUsers,
    canAssignLeads,
    canViewAllLeads
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
